import { trpcCall } from "./trpc";

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

interface AnonCreateResult {
  projectId: string;
  videoKey: string;
}

interface MultipartResult {
  uploadId: string;
  partUrls: string[];
}

export async function uploadVideo(args: {
  file: File;
  title: string;
  language: string;
  fileExtension: "mp4" | "mov";
  videoMetaData: {
    width: number;
    height: number;
    duration: number;
    fps?: number;
    colorSpace?: string;
  };
  turnstileToken: string;
  onProgress: (percent: number, projectId: string) => void;
}): Promise<string> {
  // 1. Create anonymous project
  const { projectId, videoKey } = await trpcCall<AnonCreateResult>(
    "project.anonymousCreateProject",
    {
      title: args.title,
      language: args.language,
      fileExtension: args.fileExtension,
      videoMetaData: args.videoMetaData,
      sizeInBytes: args.file.size,
      turnstileToken: args.turnstileToken,
    },
    { method: "mutation" }
  );

  // 2. Create multipart upload
  const numParts = Math.ceil(args.file.size / CHUNK_SIZE);
  const { uploadId, partUrls } = await trpcCall<MultipartResult>(
    "project.createMultipartUpload",
    {
      key: videoKey,
      contentType: args.file.type,
      numParts,
      uploadContext: "project",
      contentLengthType: "short",
    },
    { method: "mutation" }
  );

  // 3. Upload each chunk, track progress
  const etags: { PartNumber: number; ETag: string }[] = [];
  let uploaded = 0;

  const concurrency = 2;
  let cursor = 0;
  async function worker() {
    while (true) {
      const idx = cursor++;
      if (idx >= numParts) return;
      const start = idx * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, args.file.size);
      const blob = args.file.slice(start, end);
      const res = await fetch(partUrls[idx], {
        method: "PUT",
        body: blob,
      });
      if (!res.ok) throw new Error(`Chunk ${idx} upload failed`);
      const etag = res.headers.get("etag")?.replace(/"/g, "") ?? "";
      etags[idx] = { PartNumber: idx + 1, ETag: etag };
      uploaded += blob.size;
      args.onProgress(Math.round((uploaded / args.file.size) * 100), projectId);
    }
  }
  await Promise.all(Array.from({ length: concurrency }, () => worker()));

  // 4. Complete multipart
  await trpcCall(
    "project.completeMultipartUpload",
    { key: videoKey, uploadId, parts: etags },
    { method: "mutation" }
  );

  // 5. Now that the file exists in R2, kick off the processing pipeline
  await trpcCall(
    "project.anonymousStartProcessing",
    { projectId },
    { method: "mutation" }
  );

  return projectId;
}
